/* eslint-disable prefer-rest-params */

import type { AxiosError } from 'axios';
import Axios from 'axios';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';

import { appEnv, authApiHost, isLocalDevelopment } from '../app-config';
import { getQueryObj, preserveWindowQuery } from './uri';

const sent: string[] = [];

// Sliding-window rate limiter: prevent error/render loops from flooding the server.
// Silent drop — never log here (console.error is intercepted by this module).
const WINDOW_MS = 10_000;
const MAX_POSTS_PER_WINDOW = 30;
const recentSendTimestamps: number[] = [];

export function isUseless(data: ISubmitData, dataString: string): boolean {
  if (!data) {
    return true;
  }
  if (data.colno === 0 && data.lineno === 0 && !data.source && data.message === 'Script error.') {
    return true;
  }
  if (Array.isArray(data.arguments) && data.arguments[0] === 'AxiosError: Request aborted') {
    return true;
  }
  if (data.axiosErr && (data.axiosErr.code === 'ERR_NETWORK' || data.axiosErr.code === 'ECONNABORTED')) {
    return true;
  }
  if (isEqual(omit(data, ['href']), { arguments: ['AxiosError: Network Error'] })) {
    return true;
  }
  // Suppress AxiosError network noise from the unhandledrejection path (no axiosErr object there)
  if (
    data.name === 'AxiosError' &&
    typeof data.message === 'string' &&
    (data.message === 'Network Error' ||
      data.message === 'Request aborted' ||
      data.message.startsWith('Network Error') ||
      data.message.startsWith('Request aborted'))
  ) {
    return true;
  }
  // Benign "already completed" campaign-skip rejection: the backend rejects a
  // completed→skipped transition, surfaced as an unhandled ClientError rejection
  // from the fire-and-forget skip action. Expected business rule, not an app bug.
  if (data.name === 'ClientError' && data.message === "Can't change participation status from completed to skipped!") {
    return true;
  }
  return false;
}

export function isReload(data: ISubmitData, dataString: string): boolean {
  if (
    data &&
    typeof data.name === 'string' &&
    data.name === 'ChunkLoadError' &&
    typeof data.source === 'string' &&
    data.source.startsWith(window.location.origin) &&
    typeof getQueryObj().refresh === 'undefined' // do not refresh multiple times
  ) {
    window.location.href = preserveWindowQuery(
      `${window.location.origin}${window.location.pathname}?refresh=${Date.now()}`
    );
    return true;
  }
  return false;
}

function isExcluded(data: ISubmitData, dataString: string): boolean {
  if (!data || !dataString) {
    return true;
  }
  if (isEqual(omit(data, 'href'), { colno: 0, lineno: 0, source: '', message: 'Script error.' })) {
    return true;
  }
  if (typeof data.source === 'string' && data.source.startsWith('https://googleads.g.doubleclick.net/')) {
    return true;
  }
  if (typeof data.arguments?.[0] === 'string') {
    if (
      data.arguments[0].startsWith('Asset') &&
      data.arguments[0].includes('got suspended, please visit https://anytrack.io for more details.')
    ) {
      return true;
    }
    if (data.arguments[0].startsWith('Pinterest Tag Error:')) {
      return true;
    }
  }
  return sent.includes(dataString);
}

interface ISubmitData {
  href: string;
  [other: string]: any;
  arguments?: any[];
  axiosErr?: AxiosError;
}

type LogLevel = 'info' | 'warn' | 'error';

// Strip query string and hash from a URL so credential tokens carried in query
// params (e.g. ?token=…, ?pwd-token=…) never reach the remote log store.
function sanitizeUrl(raw: string): string {
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.origin}${u.pathname}`;
  } catch {
    return raw.split('?')[0].split('#')[0];
  }
}

// Only the page-location keys carry credential tokens in query params. `source`
// is a script URL (no tokens) and may be a bare filename, so it is left as-is.
const URL_ATTRIBUTE_KEYS = new Set(['href', 'url']);

function buildPayload(level: LogLevel, data: ISubmitData) {
  // Derive a single message string. NEVER JSON.stringify the whole `data`: it can
  // carry an AxiosError whose toJSON() embeds request headers (incl. the user's
  // pp-token), which would leak into the log payload.
  let message: string;
  if (typeof data.message === 'string' && data.message) {
    message = data.message;
  } else if (Array.isArray(data.arguments) && data.arguments.length > 0) {
    message = data.arguments.join(' ');
  } else if (data.axiosErr) {
    message = `${data.axiosErr.name ?? 'AxiosError'}: ${data.axiosErr.message}`;
  } else {
    message = 'Unspecified client error';
  }

  // Attributes: primitives only, omit undefined values; URLs are sanitized.
  const attributes: Record<string, string | number | boolean> = {};
  const primitiveKeys = ['href', 'name', 'source', 'stack', 'lineno', 'colno', 'url'] as const;
  for (const key of primitiveKeys) {
    const val = data[key];
    if (
      val !== undefined &&
      val !== null &&
      (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean')
    ) {
      attributes[key] = typeof val === 'string' && URL_ATTRIBUTE_KEYS.has(key) ? sanitizeUrl(val) : val;
    }
  }
  // Carry axios info if present
  if (data.axiosErr) {
    const { code, status, response } = data.axiosErr;
    if (code) {
      attributes.axiosCode = String(code);
    }
    const resolvedStatus = status ?? response?.status;
    if (resolvedStatus !== undefined) {
      attributes.axiosStatus = Number(resolvedStatus);
    }
  }

  return {
    service: 'pureprofile-app' as const,
    level,
    message,
    ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
  };
}

export function submitData(level: LogLevel, data: ISubmitData) {
  if (isLocalDevelopment) {
    return;
  }
  let dataString: string;
  try {
    dataString = JSON.stringify(data);
  } catch {
    dataString = String(data?.message ?? data?.href ?? 'unserializable');
  }
  if (isUseless(data, dataString)) {
    return;
  }
  if (isReload(data, dataString)) {
    return;
  }
  if (isExcluded(data, dataString)) {
    return;
  }
  // Rate limiter: sliding window over WINDOW_MS; silently drop when at capacity.
  const now = Date.now();
  let i = 0;
  while (i < recentSendTimestamps.length && recentSendTimestamps[i] <= now - WINDOW_MS) {
    i++;
  }
  recentSendTimestamps.splice(0, i);
  if (recentSendTimestamps.length >= MAX_POSTS_PER_WINDOW) {
    return;
  }
  recentSendTimestamps.push(now);
  // Cap dedup array to the most recent 200 entries to bound memory.
  sent.push(dataString);
  if (sent.length > 200) {
    sent.shift();
  }
  void Axios.request({
    method: 'POST',
    url: `${authApiHost}/api/v1/public/client-log`,
    data: buildPayload(level, data),
  }).catch(() => {});
}

// hook to console methods
function handleIntercepted(level: LogLevel, args: IArguments) {
  const data: ISubmitData = {
    href: window.location.href,
    arguments: Array.from(args).map((i) => i?.toString()),
  };
  submitData(level, data);
}

function takeOverConsole() {
  const { console } = window;
  if (!console) {
    return;
  }

  function intercept(level: LogLevel, method: keyof Console) {
    const original = console[method] as any;
    (console as any)[method] = function () {
      try {
        handleIntercepted(level, arguments);
      } catch (err) {
        // ignore
      }
      return original.apply
        ? // do this for normal browsers
          original.apply(console, arguments)
        : // do this for IE
          original(Array.prototype.slice.apply(arguments).join(' '));
    } as any;
  }

  // Only intercept console.error — console.warn is NOT intercepted here to avoid
  // forwarding arbitrary third-party warn noise. Explicit logWarn calls submit directly.
  intercept('error', 'error');
}
takeOverConsole();

// hook to window.onerror
const previousWindowOnError = window.onerror;
window.onerror = function (...args) {
  const [message, source, lineno, colno, error] = args;
  const data: ISubmitData = {
    href: window.location.href,
    name: error?.name,
    message,
    source,
    stack: error?.stack,
    lineno,
    colno,
  };
  submitData('error', data);
  previousWindowOnError?.(...args);
};

// hook to window.unhandledrejection
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const data: ISubmitData = {
    href: window.location.href,
    name: reason?.name,
    message: reason?.message ?? String(reason),
    stack: reason?.stack,
  };
  submitData('error', data);
});

// export logging utilities
// logError routes through console.error, which is intercepted by takeOverConsole() — no double-submit.
// logWarn calls submitData directly (console.warn is NOT intercepted) and also calls console.warn.
export const log = {
  info: (msg: string) => console.log(msg),
  warn: (msg: string) => {
    submitData('warn', { href: window.location.href, message: msg });
    console.warn(msg);
  },
  error: (msg: string) => console.error(msg),
  debug: (...args: any[]) => {
    if (appEnv === 'development') {
      console.debug(...args);
    }
  },
  trace: (msg: string) => console.trace(msg),
};
export const logInfo = log.info;
export const logWarn = log.warn;
export const logError = log.error;
export const logTrace = log.trace;
